import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Header from '../../Header'
import Footer from '../../Footer'
import { FormField } from '../../FormField'
import Keyboard from '../../Keyboard'
import { setUIForwardAction, setUIBackwardsAction } from '../../../actions'
import { setConsigneeAction } from '../../../actions/reservationChange'
import { UI } from '../../../constants/ui'
import { AppState } from '../../../reducers'
import CustomCaret, { CustomCaretProps } from '../../CustomCaret'
import './consignee.less'
import { useViewport } from '../../../hooks/responsive'
import FooterMobile from '../../FooterMobile'

const mapDispatchToProps = {
  setUIForward: setUIForwardAction,
  setUIBackwards: setUIBackwardsAction,
  setConsignee: setConsigneeAction,
}
type DispatchProps = typeof mapDispatchToProps
const mapStateToProps = (state: AppState) => ({
  consignee: state.reservationChange.consignee,
})

type StateProps = ReturnType<typeof mapStateToProps>
type Props = DispatchProps & StateProps

interface CustomProps {
  clearInputField: (currentInput: any) => void
  consigneeInput: React.RefObject<HTMLInputElement>
  activeInput: HTMLInputElement | undefined
  setActiveInput: (input: any) => void
  onClick: () => void
  onChange: (value: string) => void
  refConsigneeCaret: React.Ref<any>
  isKeyboardShown: boolean
  setShowKeyboard: (value: boolean) => void
}

type MobileProps = Pick<Props, "setUIBackwards" | "consignee" | "setUIForward"> & Pick<CustomProps, 'clearInputField' | 'onChange'>

const ConsigneeMobile: React.FC<MobileProps> = ({ setUIBackwards, setUIForward, consignee, clearInputField, onChange }) => {
  const { t } = useTranslation()

  return (
    <div className="consignee-mobile">
      <div className="consignee-mobile-wrapper">
        <div className="consignee-title" dangerouslySetInnerHTML={{ __html: t('consignee') }}></div>
        <div className="consignee-info">
          <FormField
            id="consignee"
            label={t('consigneeFinalReceiver')}
            isClearNeed={!!consignee}
            clearInputField={clearInputField}
          >
            <input
              autoFocus
              spellCheck={false}
              className="input-field-mobile"
              value={consignee}
              onChange={(e) => onChange(e.target.value)}
            />
          </FormField>
          <div className="consignee-main-button">
            <button
              className="main-button-mobile"
              onClick={() => setUIForward(UI.FROM_TO)}
              disabled={!consignee || !consignee.trim()}
            >
              {t('next')}
            </button>
          </div>
        </div>
      </div>
      <div className="footer-mobile-container">
        <FooterMobile showPageCount back={() => setUIBackwards(UI.CARGO_WEIGHT)} />
      </div>
    </div>
  )
}

type DesktopProps = Pick<Props, "setUIBackwards" | "consignee" | "setUIForward"> & CustomProps

const ConsigneeDesktop: React.FC<DesktopProps> = ({ setUIBackwards, setUIForward, consignee, clearInputField, consigneeInput, onChange, setActiveInput, onClick, refConsigneeCaret, isKeyboardShown, activeInput, setShowKeyboard }) => {
  const { t } = useTranslation()

  return (
    <React.Fragment>
      <Header showPageCount back={() => setUIBackwards(UI.CARGO_WEIGHT)} />
      <div className="consignee">
        <div className="consignee-title" dangerouslySetInnerHTML={{ __html: t('consignee') }}></div>
        <div className="consignee-info">
          <FormField
            id="consignee"
            label={t('consigneeFinalReceiver')}
            isClearNeed={!!consignee}
            clearInputField={clearInputField}
            currentInput={consigneeInput.current}
          >
            <>
              <input
                autoFocus
                spellCheck={false}
                ref={consigneeInput}
                className="input-field input-consignee"
                value={consignee}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => {
                  setActiveInput(consigneeInput.current)
                }}
                onClick={onClick}
              />
              <CustomCaret ref={refConsigneeCaret} inputRef={consigneeInput.current} />
            </>
          </FormField>
          <div className="consignee-main-button">
            <button
              className="main-button"
              onClick={() => setUIForward(UI.FROM_TO)}
              disabled={!consignee || !consignee.trim()}
            >
              {t('next')}
            </button>
          </div>
        </div>
      </div>
      <div className="keyboard-cont">
        {isKeyboardShown && (
          <Keyboard input={activeInput} onChange={onChange} format={''} onClose={() => setShowKeyboard(false)} />
        )}
      </div>
      <div className="footer-cont">{!isKeyboardShown && <Footer />}</div>
    </React.Fragment>
  )
}

const Consignee: React.FC<Props> = ({ setUIForward, setUIBackwards, setConsignee, consignee }: Props) => {
  const [activeInput, setActiveInput] = useState()
  const [isKeyboardShown, setShowKeyboard] = useState(true)
  const consigneeInput = useRef(null)
  const refConsigneeCaret = useRef<CustomCaretProps>(null)
  const { isMobile } = useViewport()

  const updateCaretPosition = () => {
    refConsigneeCaret.current && refConsigneeCaret.current.updateCaretPosition()
  }

  const onChange = (value: string) => {
    setConsignee(value)
    updateCaretPosition()
  }

  const onClick = () => {
    setShowKeyboard(true)
    updateCaretPosition()
  }

  const clearInputField = (currentInput: any) => {
    setActiveInput(currentInput)
    setConsignee('')
  }

  useEffect((): void => {
    consigneeInput && setActiveInput(consigneeInput.current)
  }, [])

  if (isMobile) {
    const mobileProps = { setUIBackwards, setUIForward, consignee, clearInputField, onChange }
    return <ConsigneeMobile {...mobileProps} />
  } else {
    const desktopProps = { setUIBackwards, setUIForward, consignee, clearInputField, consigneeInput, onChange, setActiveInput, onClick, refConsigneeCaret, isKeyboardShown, activeInput, setShowKeyboard }
    return <ConsigneeDesktop {...desktopProps} />
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Consignee)
